import api from '@/lib/axios';
import type { PaginatedResult } from '@/components/data-table';

export interface QuranTargetRow {
  id: string;
  classId: string;
  className: string;
  surahId: string;
  surahName: string;
  ayathFrom: number;
  ayathTo: number;
  createdAt: string;
}

export interface QuranStudentTargetRow {
  id: string;
  targetId: string;
  className: string;
  surahName: string;
  ayathFrom: number;
  ayathTo: number;
  studentId: string;
  studentName: string;
  admissionNo: string;
  assignedTeacherId: string | null;
  assignedTeacherName: string | null;
  assignedAyath: string | null;
  completedAyath: string | null;
  status: string;
  completedPercent: number;
  remarks: string | null;
  assignedDate: string | null;
  completedDate: string | null;
  targetDate: string | null;
  grade: string | null;
}

export interface QuranTrackerResponseRow {
  id: string;
  targetId: string;
  className: string;
  surahName: string;
  studentId: string;
  studentName: string;
  ayathList: string;
  status: string;
  grade: string | null;
  evaluationRemarks: string | null;
  evaluationBy: string | null;
  url: string | null;
  createdAt: string;
}

export interface SurahPickerRow {
  id: string;
  nameEng: string;
  nameArabic: string;
  ayathCount: number;
}

export interface CreateQuranTargetPayload {
  classId: string;
  surahId: string;
  ayathFrom: number;
  ayathTo: number;
}

export interface CreateQuranStudentTargetPayload {
  targetId: string;
  studentId: string;
  assignedTeacherId?: string;
  assignedAyath?: string;
  assignedDate?: string;
  targetDate?: string;
}

export interface UpdateQuranStudentTargetPayload {
  completedAyath?: string;
  status?: string;
  completedPercent?: number;
  remarks?: string;
  completedDate?: string;
  grade?: string;
}

export interface TeacherPickerRow {
  id: string;
  name: string;
}

export interface OrgClassPickerRow {
  id: string;
  name: string;
  sections: { id: string; name: string }[];
}

export interface OrgStudentPickerRow {
  id: string;
  admissionNo: string;
  name: string;
  className: string;
  sectionName: string;
}

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export type QuranTrackerScope = 'school' | 'organization';

/**
 * School-tier hits `/quran-targets`, `/quran-student-targets`, `/quran-
 * tracker-responses` (TenantJwtGuard, the schema comes from the JWT).
 * Organization-tier hits `/org/quran-tracker/*` (OrgScopedGuard — same
 * QuranTrackerService underneath, just resolved to the org's own
 * operating school server-side; see OrgQuranTrackerController). The real
 * legacy Super Admin session was tied to exactly one school, never a
 * cross-school picker, so org-tier is a single-school view like school-
 * tier — just a different base path, not different shape.
 */
export function createQuranTrackerApi(scope: QuranTrackerScope) {
  const isOrg = scope === 'organization';
  const targetsBase = isOrg ? '/org/quran-tracker/targets' : '/quran-targets';
  const studentTargetsBase = isOrg ? '/org/quran-tracker/student-targets' : '/quran-student-targets';
  const responsesBase = isOrg ? '/org/quran-tracker/tracker-responses' : '/quran-tracker-responses';

  return {
    // QT Target
    listTargets: async (params?: { page?: number; limit?: number; classId?: string }): Promise<PaginatedResult<QuranTargetRow>> =>
      unwrap(await api.get(targetsBase, { params })),
    createTarget: async (payload: CreateQuranTargetPayload): Promise<QuranTargetRow> =>
      unwrap(await api.post(targetsBase, payload)),
    surahsPicker: async (): Promise<SurahPickerRow[]> => unwrap(await api.get(`${targetsBase}/pickers/surahs`)),
    /** Org-tier only — the tenant-side pages use AcademicClassesApi.list() instead (TenantJwtGuard-only). */
    classesPicker: async (): Promise<OrgClassPickerRow[]> => unwrap(await api.get('/org/quran-tracker/pickers/classes')),
    /** Org-tier only — the tenant-side pages use FeesApi.studentsPicker() instead (TenantJwtGuard-only). */
    studentsPicker: async (params: { classId?: string; sectionId?: string; search?: string }): Promise<OrgStudentPickerRow[]> =>
      unwrap(await api.get(`${studentTargetsBase}/pickers/students`, { params })),

    // QT Students Target
    listStudentTargets: async (params?: {
      page?: number;
      limit?: number;
      studentId?: string;
      classId?: string;
      status?: string;
    }): Promise<PaginatedResult<QuranStudentTargetRow>> => unwrap(await api.get(studentTargetsBase, { params })),
    createStudentTarget: async (payload: CreateQuranStudentTargetPayload): Promise<QuranStudentTargetRow> =>
      unwrap(await api.post(studentTargetsBase, payload)),
    updateStudentTarget: async (id: string, payload: UpdateQuranStudentTargetPayload): Promise<QuranStudentTargetRow> =>
      unwrap(await api.patch(`${studentTargetsBase}/${id}`, payload)),
    /** Org-tier only — the org admin is never a teacher, so assignedTeacherId must be picked explicitly. */
    teachersPicker: async (): Promise<TeacherPickerRow[]> => unwrap(await api.get(`${studentTargetsBase}/pickers/teachers`)),

    // Tracker responses
    listTrackerResponses: async (params?: {
      page?: number;
      limit?: number;
      studentId?: string;
      targetId?: string;
    }): Promise<PaginatedResult<QuranTrackerResponseRow>> => unwrap(await api.get(responsesBase, { params })),
  };
}

export const QuranTrackerApi = createQuranTrackerApi('school');
